import {
  DASHBOARD_SECTIONS_ROUTES,
  DashboardPermissions,
  DOCUMENTS_PERMISSIONS,
  PAYOUT_ORDER_PERMISSIONS,
  STATISTICS_PERMISSIONS,
} from '@/models/common/permissions.model';
import { defineStore } from 'pinia';
import { PayoutOrderPermissionGetter } from '@/models/payout-order/payout-order-permission-getter.model';

interface PermissionsStore {
  dashboardPermissions: DashboardPermissions;
}

export const usePermissionsStore = defineStore('PermissionsStore',
  {
    state: (): PermissionsStore => ({
      dashboardPermissions: {
        [DASHBOARD_SECTIONS_ROUTES.STATISTICS]: {
          [STATISTICS_PERMISSIONS.GET_PLAYERS_REPORT_COUNT]: false,
          [STATISTICS_PERMISSIONS.GET_STATISTICS_REPORT_LIST]: false,
          [STATISTICS_PERMISSIONS.GET_TOP_WINS_HISTORY_LIST]: false,
        },
        [DASHBOARD_SECTIONS_ROUTES.PAYOUT_ORDER]: {
          [PAYOUT_ORDER_PERMISSIONS.GET_PAYMENT_MODERATION_ACTION_LIST]: false,
          [PAYOUT_ORDER_PERMISSIONS.GET_TRANSACTION_MODERATION_INFO]: false,
          [PAYOUT_ORDER_PERMISSIONS.GET_PAYOUT_ORDER_LIST]: false,
          [PAYOUT_ORDER_PERMISSIONS.GET_PAYMENT_LIST]: false,
          [PAYOUT_ORDER_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO_FORCE_TAKE]: false,
          [PAYOUT_ORDER_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO_MANUAL_FLOW]: false,
          [PAYOUT_ORDER_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO]: false,
        },
        [DASHBOARD_SECTIONS_ROUTES.DOCUMENTS]: {
          [DOCUMENTS_PERMISSIONS.GET_DOCUMENT_LIST]: false,
          [DOCUMENTS_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO_FLOW]: false,
          [DOCUMENTS_PERMISSIONS.GET_DOCUMENT_MODERATION_FILTER_STATUS_LIST]: false,
        },
      },
    }),
    getters: {
      statisticsPermission(state: PermissionsStore) {
        const permissions = state.dashboardPermissions[DASHBOARD_SECTIONS_ROUTES.STATISTICS];

        return {
          isGetPlayersReportCountAllowed: permissions[STATISTICS_PERMISSIONS.GET_PLAYERS_REPORT_COUNT],
          isGetStatisticsReportListAllowed: permissions[STATISTICS_PERMISSIONS.GET_STATISTICS_REPORT_LIST],
          isGetTopWinsHistoryListAllowed: permissions[STATISTICS_PERMISSIONS.GET_TOP_WINS_HISTORY_LIST],
        };
      },
      payoutOrderPermissions(state: PermissionsStore): PayoutOrderPermissionGetter {
        const permissions = state.dashboardPermissions[DASHBOARD_SECTIONS_ROUTES.PAYOUT_ORDER];

        return {
          isGetPaymentModerationActionListAllowed:
          permissions[PAYOUT_ORDER_PERMISSIONS.GET_PAYMENT_MODERATION_ACTION_LIST],
          isGetTransactionModerationInfoAllowed:
          permissions[PAYOUT_ORDER_PERMISSIONS.GET_TRANSACTION_MODERATION_INFO],
          isGetPayoutOrderListAllowed:
          permissions[PAYOUT_ORDER_PERMISSIONS.GET_PAYOUT_ORDER_LIST],
          isGetPaymentListAllowed:
          permissions[PAYOUT_ORDER_PERMISSIONS.GET_PAYMENT_LIST],
          isForceTakeAllowed:
          permissions[PAYOUT_ORDER_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO_FORCE_TAKE],
          isManualFlowAllowed:
          permissions[PAYOUT_ORDER_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO_MANUAL_FLOW],
          isTransactionModerationInfo: permissions[PAYOUT_ORDER_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO],
        };
      },
      documentsPermission(state: PermissionsStore) {
        const permissions = state.dashboardPermissions[DASHBOARD_SECTIONS_ROUTES.DOCUMENTS];

        return {
          isGetDocumentListAllowed: permissions[DOCUMENTS_PERMISSIONS.GET_DOCUMENT_LIST],
          isUpdateTransactionModerationInfoFlowAllowed:
            permissions[DOCUMENTS_PERMISSIONS.UPDATE_TRANSACTION_MODERATION_INFO_FLOW],
          isGetDocumentModerationFilterStatusListAllowed:
            permissions[DOCUMENTS_PERMISSIONS.GET_DOCUMENT_MODERATION_FILTER_STATUS_LIST],
        };
      },
    },
    actions: {
      setPermissions(permissions: DashboardPermissions) {
        this.dashboardPermissions = permissions;
      },
      checkPermission(permission: string) {
        const actualSections = Object.keys(this.dashboardPermissions);

        return actualSections.some((section) => {
          return this.dashboardPermissions[section as DASHBOARD_SECTIONS_ROUTES][permission];
        });
      },
    },
  });
